/**
 * MedListRecordUpdate: after a medication list linked to a client is
 * saved, its rows offered to the client's Medications card. Shown by the
 * Medication List tool and the record's "Make med list" dialog, under the
 * saved line.
 *
 * Each row the list holds (named, not marked stopped) is checked against
 * the record by the server (matchMedListToRecord, the photo door's rules)
 * and shown with what it is:
 *   - "Already on the record": nothing to do;
 *   - on the record with a change ("The list differs on Dose, Prescriber"):
 *     "Update {X}" or "Leave as is";
 *   - "Did you mean {X}, already on the record?": "Update {X}", "Keep both"
 *     or "Skip", never picked for the person;
 *   - "New": "Add {name}" or "Skip"; "Add {n} to the record" adds every
 *     new row still waiting, one at a time in list order.
 * Nothing is written until the care manager presses one of those. Each
 * write goes through the record's own add or edit route; a refusal shows
 * on its row in the record form's words (recordEditErrorMessage), and the
 * row stays to try again.
 *
 * A seat that may not open the client's record gets no panel (the check
 * answers null).
 */

import { type ReactNode, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { MedicationRow } from '@/lib/clientRecord';
import type { MedListItem } from '@/lib/medList';
import { medicationRowValue } from '@/lib/confirmQueueActions';
import { recordFieldLabel } from '@/lib/recordFormWords';
import { PROPOSAL_PAYLOAD_SPECS } from '@/lib/noteVocabulary';
import {
  RECORD_ADD_PROJECTIONS,
  patchRecordRow,
  postRecordRow,
  recordEditErrorMessage,
  type RecordEditErrorContext,
} from '@/lib/clientRecordEditApi';
import {
  addFieldsFor,
  matchMedListToRecord,
  matchRowOf,
  offerableItems,
  recordOffers,
  updateFieldsFor,
  type MedListRecordOffer,
  type RecordTarget,
} from '@/lib/medListRecordSync';

export interface MedListRecordUpdateProps {
  clientId: string;
  /** The saved list's rows. */
  items: readonly MedListItem[];
  /** The client's medications as the page holds them. */
  record: readonly MedicationRow[];
}

type RowOutcome = 'added' | 'updated' | 'skipped';

interface RowState {
  busy: boolean;
  outcome: RowOutcome | null;
  error: string | null;
}

const IDLE: RowState = { busy: false, outcome: null, error: null };

const OUTCOME_WORDS: Record<RowOutcome, string> = {
  added: 'Added to the record',
  updated: 'Updated on the record',
  skipped: 'Skipped',
};

function errorContextFor(sent: Record<string, unknown>): RecordEditErrorContext {
  return { specs: PROPOSAL_PAYLOAD_SPECS[RECORD_ADD_PROJECTIONS.medications.specType], sent };
}

function itemValue(item: MedListItem): string {
  return medicationRowValue({
    name: item.name.trim(),
    dose: item.dose.trim() === '' ? null : item.dose.trim(),
  });
}

function targetValue(target: RecordTarget): string {
  return medicationRowValue({ name: target.name, dose: target.dose ?? null });
}

function targetName(target: RecordTarget): string {
  return medicationRowValue({ name: target.name, dose: null });
}

export function MedListRecordUpdate({
  clientId,
  items,
  record,
}: MedListRecordUpdateProps): ReactNode {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const offered = offerableItems(items);
  const [rows, setRows] = useState<Record<number, RowState>>({});
  // The record as it stood when the list was saved: each write refetches
  // the record, and a row must not change what it offers under the hand
  // that just pressed it.
  const [savedAgainst] = useState(record);

  const match = useQuery({
    queryKey: ['med-list', 'record-match', tenantSlug, clientId, offered.map(matchRowOf)],
    queryFn: () => matchMedListToRecord(tenantSlug, clientId, offered),
    enabled: offered.length > 0,
    // The answer is read once per saved list: a refetch while the person
    // works would move rows under their hands.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const patchRow = (i: number, patch: Partial<RowState>): void =>
    setRows((prev) => ({ ...prev, [i]: { ...(prev[i] ?? IDLE), ...patch } }));

  /** One row's add or update, through the record's own route. The row
   *  shows its own result; a refusal stays on it to try again. */
  async function run(
    i: number,
    kind: 'add' | 'update',
    offer: MedListRecordOffer,
    target?: RecordTarget,
  ): Promise<void> {
    const fields = kind === 'add' ? addFieldsFor(offer.item) : updateFieldsFor(offer.item, target!);
    patchRow(i, { busy: true, error: null });
    try {
      if (kind === 'add') await postRecordRow(tenantSlug, clientId, 'medications', fields);
      else
        await patchRecordRow(tenantSlug, clientId, 'medications', target!.id, {
          action: 'edit',
          fields,
        });
    } catch (err) {
      patchRow(i, {
        busy: false,
        error: recordEditErrorMessage(err, errorContextFor(fields), contact),
      });
      return;
    }
    patchRow(i, { busy: false, outcome: kind === 'add' ? 'added' : 'updated' });
    await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
  }

  if (offered.length === 0) return null;
  if (match.isPending) {
    return (
      <p className="text-sm text-ink-soft" role="status" data-testid="med-list-record-checking">
        Checking the client&apos;s medications…
      </p>
    );
  }
  if (match.isError) {
    return (
      <ErrorBanner
        error={match.error}
        what="the client's medications"
        onRetry={() => void match.refetch()}
        data-testid="med-list-record-error"
      />
    );
  }
  if (match.data === null) return null;

  const offers = recordOffers(items, match.data, savedAgainst);
  const waitingNew = offers
    .map((o, i) => ({ o, i }))
    .filter(
      ({ o, i }) =>
        o.state === 'new' && (rows[i] ?? IDLE).outcome === null && !(rows[i] ?? IDLE).busy,
    );
  const allOnRecord = offers.every((o) => o.state === 'on_record');

  async function addAll(): Promise<void> {
    for (const { o, i } of waitingNew) await run(i, 'add', o);
  }

  return (
    <section
      className="rounded-card border border-sage-800/[8%] bg-paper p-4"
      aria-labelledby="med-list-record-heading"
      data-testid="med-list-record-update"
    >
      <h3 id="med-list-record-heading" className="font-display text-lg text-sage-800">
        Update the client&apos;s medications
      </h3>
      {allOnRecord ? (
        <p className="mt-1 text-sm text-ink-soft" data-testid="med-list-record-all-on">
          Every medication on this list is already on the record.
        </p>
      ) : (
        <p className="mt-1 text-sm text-ink-soft">
          Choose what goes on the Medications card. Nothing on the record changes until you add or
          update it.
        </p>
      )}
      {!allOnRecord && (
        <ul className="mt-3 flex flex-col gap-2">
          {offers.map((offer, i) => (
            <OfferRow
              key={i}
              offer={offer}
              state={rows[i] ?? IDLE}
              onAdd={() => void run(i, 'add', offer)}
              onUpdate={(target) => void run(i, 'update', offer, target)}
              onSkip={() => patchRow(i, { outcome: 'skipped', error: null })}
            />
          ))}
        </ul>
      )}
      {waitingNew.length > 1 && (
        <div className="mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void addAll()}
            data-testid="med-list-record-add-all"
          >
            {`Add ${waitingNew.length} to the record`}
          </Button>
        </div>
      )}
    </section>
  );
}

function OfferRow({
  offer,
  state,
  onAdd,
  onUpdate,
  onSkip,
}: {
  offer: MedListRecordOffer;
  state: RowState;
  onAdd: () => void;
  onUpdate: (target: RecordTarget) => void;
  onSkip: () => void;
}): ReactNode {
  const name = itemValue(offer.item);

  if (state.outcome !== null) {
    return (
      <li
        className="flex items-center gap-2 rounded-base border border-sage-800/[6%] bg-sage-50 px-3 py-2"
        data-testid="med-list-record-row"
        data-state={offer.state}
        data-outcome={state.outcome}
      >
        {state.outcome !== 'skipped' && (
          <Check size={16} className="shrink-0 text-sage-700" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-sage-800">{name}</span>
        <span className="text-xs text-ink-soft">{OUTCOME_WORDS[state.outcome]}</span>
      </li>
    );
  }

  return (
    <li
      className="rounded-base border border-sage-800/[8%] px-3 py-2.5"
      data-testid="med-list-record-row"
      data-state={offer.state}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-semibold text-sage-800">{name}</span>
        {offer.state === 'on_record' && (
          <span className="text-xs text-ink-soft">Already on the record</span>
        )}
        {offer.state === 'new' && <span className="text-xs font-semibold text-sage-800">New</span>}
      </div>

      {offer.state === 'differs' && (
        <p className="mt-1 text-sm text-sage-800" data-testid="med-list-record-changes">
          {`Already on the record. The list differs on ${offer.changes.map((f) => recordFieldLabel('medications', f)).join(', ')}.`}
        </p>
      )}
      {offer.state === 'near' &&
        offer.targets.map((t) => (
          <p
            key={t.id}
            className="mt-1 text-sm text-sage-800"
            data-testid="med-list-record-did-you-mean"
          >
            Did you mean {targetValue(t)}, already on the record?
          </p>
        ))}

      {offer.state !== 'on_record' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {offer.state === 'new' && (
            <Button
              variant="primary"
              size="sm"
              onClick={onAdd}
              loading={state.busy}
              disabled={state.busy}
              data-testid="med-list-record-add"
            >
              {`Add ${name}`}
            </Button>
          )}
          {offer.state === 'differs' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onUpdate(offer.target)}
              loading={state.busy}
              disabled={state.busy}
              data-testid={`med-list-record-update-${offer.target.id}`}
            >
              {`Update ${targetName(offer.target)}`}
            </Button>
          )}
          {offer.state === 'near' && (
            <>
              {offer.targets.map((t) => (
                <Button
                  key={t.id}
                  variant="secondary"
                  size="sm"
                  onClick={() => onUpdate(t)}
                  disabled={state.busy}
                  data-testid={`med-list-record-update-${t.id}`}
                >
                  {`Update ${targetName(t)}`}
                </Button>
              ))}
              <Button
                variant="secondary"
                size="sm"
                onClick={onAdd}
                loading={state.busy}
                disabled={state.busy}
                data-testid="med-list-record-keep-both"
              >
                Keep both
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            disabled={state.busy}
            data-testid="med-list-record-skip"
          >
            {offer.state === 'differs' ? 'Leave as is' : 'Skip'}
          </Button>
        </div>
      )}
      {state.error !== null && (
        <p
          className="mt-1.5 text-xs text-pepper"
          role="alert"
          data-testid="med-list-record-row-error"
        >
          {state.error}
        </p>
      )}
    </li>
  );
}
